import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Trophy, Bell, Gift, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandLogo } from '../components/layout/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls = 'h-12 rounded-xl border-white/[0.08] bg-[#09090b] text-white placeholder:text-[#52525b] focus-visible:ring-[#EAB308]';
const labelCls = 'text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]';

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: '', whatsapp_number: '', password: '', referral_code: '' });
  const [loading, setLoading] = useState(false);
  const [hasReferral, setHasReferral] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setFormData((prev) => ({ ...prev, referral_code: refCode }));
      setHasReferral(true);
      setIsLogin(false);
      toast.success('Referral code applied! Sign up to claim your spot.', { duration: 5000 });
    } else if (params.get('join') === '1' || window.location.pathname.replace(/\/$/, '').endsWith('/join')) {
      setIsLogin(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { whatsapp_number: formData.whatsapp_number, password: formData.password }
        : { name: formData.name, whatsapp_number: formData.whatsapp_number, access_code: formData.password, referral_code: formData.referral_code || null };
      const response = await axios.post(`${API}${endpoint}`, payload);
      if (!isLogin && formData.referral_code) window.history.replaceState({}, document.title, window.location.pathname);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      onLogin(response.data);
    } catch (error) {
      if (error.response) {
        const detail = error.response.data?.detail || 'An error occurred';
        // Unknown member on login -> guide them to sign up.
        if (isLogin && /sign up/i.test(detail)) {
          setIsLogin(false);
          toast.info('Looks like you\u2019re new here \u2014 sign up with the club access code.', { duration: 5000 });
        } else {
          toast.error(detail);
        }
      } else if (error.request) toast.error('Cannot reach server. Check your internet connection.');
      else toast.error('Login failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#09090b] text-[#fafafa]">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-72 w-[140%] -translate-x-1/2 rounded-full bg-[#EAB308]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 h-72 w-72 rounded-full bg-[#A855F7]/10 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 pt-safe sm:px-8 md:grid md:grid-cols-2 md:items-center md:gap-16">
        {/* Brand / hero */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="pt-10 pb-8 md:py-0"
        >
          <BrandLogo size="lg" />
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#EAB308]">Your lucky ticket awaits</p>
          <p className="mt-5 hidden max-w-md text-base leading-relaxed text-[#a1a1aa] md:block">
            Join exciting giveaways and win amazing prizes. Get digital raffle tickets and instant in-app notifications when a draw goes live.
          </p>
          <div className="mt-8 hidden gap-3 md:flex md:flex-col">
            {[
              [Ticket, 'Digital raffle tickets'],
              [Trophy, 'Live draws & real prizes'],
              [Bell, 'Instant in-app notifications'],
            ].map(([Icon, text]) => (
              <div key={text} className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#EAB308]/10">
                  <Icon className="h-4 w-4 text-[#EAB308]" />
                </span>
                <span className="text-sm text-[#fafafa]">{text}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="pb-10 md:pb-0"
        >
          <div className="rounded-3xl border border-white/[0.06] bg-[#18181b] p-5 sm:p-8">
            {/* Segmented control */}
            <div className="relative grid grid-cols-2 rounded-full bg-[#09090b] p-1" data-testid="auth-mode-switch">
              {['Login', 'Sign up'].map((label, i) => {
                const active = (i === 0) === isLogin;
                return (
                  <button
                    key={label}
                    type="button"
                    data-testid={active ? undefined : 'toggle-auth-mode-btn'}
                    onClick={() => setIsLogin(i === 0)}
                    className={`tap-none relative z-10 h-10 rounded-full text-sm font-semibold transition-colors ${active ? 'text-black' : 'text-[#a1a1aa]'}`}
                  >
                    {active && (
                      <motion.span layoutId="auth-seg" className="absolute inset-0 rounded-full bg-[#EAB308]" transition={{ type: 'spring', stiffness: 500, damping: 40 }} />
                    )}
                    <span className="relative">{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-xl font-semibold tracking-tight text-white sm:text-2xl">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </h2>
              <p className="mt-1 text-sm text-[#a1a1aa]">{isLogin ? 'Enter your number and the club access code' : 'Enter your details and the club access code'}</p>
            </div>

            {!isLogin && hasReferral && (
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#10b981]/30 bg-[#10b981]/10 px-3.5 py-3">
                <Gift className="h-5 w-5 shrink-0 text-[#10b981]" />
                <div>
                  <div className="text-sm font-semibold text-[#10b981]">Referred by a friend</div>
                  <div className="text-xs text-[#a1a1aa]">£5 is added to prize pots when you join</div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              {!isLogin && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className={labelCls}>Full name</Label>
                  <Input id="name" data-testid="register-name-input" placeholder="Your name" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className={inputCls} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="whatsapp" className={labelCls}>WhatsApp number</Label>
                <Input id="whatsapp" data-testid="auth-whatsapp-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="e.g. 07123 456789"
                  value={formData.whatsapp_number} onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })} required className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className={labelCls}>Club access code</Label>
                <Input id="password" data-testid="auth-password-input" type="password" autoComplete="off" placeholder="Enter the members club code"
                  value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className={inputCls} />
              </div>

              <Button
                type="submit"
                data-testid={isLogin ? 'login-submit-btn' : 'register-submit-btn'}
                disabled={loading}
                className="mt-2 h-12 w-full rounded-full bg-[#EAB308] font-semibold text-black hover:bg-[#CA8A04] active:scale-[0.98]"
              >
                {loading ? 'Please wait…' : isLogin ? 'Log in' : 'Create account'}
                {!loading && <ArrowRight className="ml-1.5 h-4 w-4" />}
              </Button>
            </form>

            {isLogin && (
              <p className="mt-4 text-center text-xs text-[#71717a]">
                Don't have the access code? <span className="text-[#EAB308]">Ask the admin for it.</span>
              </p>
            )}

            <div className="mt-5 border-t border-white/[0.06] pt-4 text-center">
              <a href="/admin-login" data-testid="admin-login-link" className="text-xs font-medium text-[#A855F7] transition-colors hover:text-[#c084fc]">
                Admin login
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
